import React, { useState, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Item } from './Item';
import { Tier as TierType } from '../types';
import { GripVertical, Edit2, Trash2, Check } from 'lucide-react';
import { useTierListStore } from '../store/tierListStore';

interface TierProps {
  tier: TierType;
  onDelete: () => void;
  tierListId: string;
}

export const Tier: React.FC<TierProps> = ({ tier, onDelete, tierListId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(tier.name);
  const [editColor, setEditColor] = useState(tier.color);
  const inputRef = useRef<HTMLInputElement>(null);
  const { dispatch } = useTierListStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: tier.id,
    data: {
      type: 'tier',
    },
  });

  const { setNodeRef: setDroppableRef } = useDroppable({
    id: tier.id,
    data: {
      tierId: tier.id,
      type: 'droppable',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
    touchAction: 'none',
  };

  const handleSave = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (editName.trim() === '') return;

    dispatch({
      type: 'UPDATE_TIER',
      payload: {
        tierListId,
        tier: {
          id: tier.id,
          name: editName,
          color: editColor,
        },
      },
    });
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditName(tier.name);
      setEditColor(tier.color);
      setIsEditing(false);
    }
  };

  return (
    <div ref={setNodeRef} className="flex gap-1.5 sm:gap-3 items-stretch" style={style}>
      <div 
        className="flex items-center touch-none" 
        {...attributes} 
        {...listeners}
      >
        <div className="cursor-grab px-1 sm:px-2 opacity-40 hover:opacity-100 transition-opacity">
          <GripVertical size={16} className="sm:w-5 sm:h-5" />
        </div>
      </div>
      <div
        className="w-20 sm:w-28 p-2 sm:p-3 flex items-center justify-between rounded-lg text-white font-bold relative"
        style={{ backgroundColor: editColor }}
      >
        {isEditing ? (
          <form onSubmit={handleSave} className="flex flex-col gap-1.5 w-full">
            <input
              ref={inputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-1.5 py-0.5 text-black rounded text-sm"
              autoFocus
            />
            <input
              type="color"
              value={editColor}
              onChange={(e) => setEditColor(e.target.value)}
              className="w-full h-5 sm:h-6 cursor-pointer"
            />
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-1 bg-white/20 hover:bg-white/30 rounded py-0.5"
            >
              <Check size={14} />
            </button>
          </form>
        ) : (
          <div className="flex items-center justify-between w-full">
            <span className="text-sm sm:text-base truncate">{tier.name}</span>
            <div className="flex gap-0.5">
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 hover:bg-black/20 rounded transition-colors"
              >
                <Edit2 size={12} className="sm:w-3.5 sm:h-3.5" />
              </button>
              <button
                onClick={onDelete}
                className="p-1 hover:bg-black/20 rounded transition-colors"
              >
                <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
      <div
        ref={setDroppableRef}
        className="flex-1 bg-gray-200 dark:bg-gray-700/50 p-2 sm:p-4 rounded-lg min-h-[90px] sm:min-h-[100px]"
      >
        <SortableContext items={tier.items.map(item => item.id)} strategy={horizontalListSortingStrategy}>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {tier.items.map((item) => (
              <Item key={item.id} item={item} tierId={tier.id} />
            ))}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};